import { Domain as AuthDomain } from 'auth-common/domain';
import type {} from 'dayjs';
import type { Tagged, ValueOf } from 'type-fest';
import { z } from 'zod';
import { dayjs } from '../../../../shared/utils/date';
import { ImmutableEntity } from '../../common';
import type { CommandContext } from '../../common/context';
import { UniqueShortId } from '../../common/id';
import { FileMetadata } from '../fileMetadata';
import { ImportTableDefinitionRevision } from '../importTableDefinitionRevision';

import Project = AuthDomain.Object.Project;

const uuidGenerator = UniqueShortId.GetFactory(21);

/** リクエスト元 */
const SourceCode = {
  Upload: 'Upload',
  Collector: 'Collector',
} as const;
type _Source = ValueOf<typeof SourceCode>;

/** タスク状態 */
const StatusCode = {
  Requested: 'Requested',
  Started: 'Started',
  Finished: 'Finished',
} as const;
type _Status = ValueOf<typeof StatusCode>;

/** 処理結果種別 */
const ResultTypeCode = {
  Succeeded: 'Succeeded',
  PartiallyFailed: 'PartiallyFailed',
  Failed: 'Failed',
} as const;
type _ResultType = ValueOf<typeof ResultTypeCode>;

export namespace ImportTask {
  export type Id = Tagged<UniqueShortId, 'importTaskId'>;
  export namespace Id {
    export const Generate = () => uuidGenerator.generate() as Id;
    export function FromString(id: string): Id {
      return uuidGenerator.FromString(id) as Id;
    }
  }

  export type Source = _Source;
  export namespace Source {
    export const FromString = (s: string): Source => {
      const value = SourceCode[s as keyof typeof SourceCode];
      if (!value) throw new Error(`Invalid ImportTask.Source: ${s}`);
      return value;
    };
  }

  export type Status = _Status;
  export namespace Status {
    export const FromString = (s: string): Status => {
      const value = StatusCode[s as keyof typeof StatusCode];
      if (!value) throw new Error(`Invalid ImportTask.Status: ${s}`);
      return value;
    };
  }

  export type ResultType = _ResultType;
  export namespace ResultType {
    export const FromString = (s: string): ResultType => {
      const value = ResultTypeCode[s as keyof typeof ResultTypeCode];
      if (!value) throw new Error(`Invalid ImportTask.ResultType: ${s}`);
      return value;
    };
  }

  /** 処理中の進捗詳細の型 */
  export type ProgressJson = {
    // load → staging → diffing → planning は apply 前の前段フェーズ (行レベルの % は出せないが、
    // どの段にいるかを front に伝えて「固まって見える」状態を防ぐ)。processing = apply 本体。sweeping = 不要データ削除。
    phase: 'loading' | 'staging' | 'diffing' | 'planning' | 'processing' | 'sweeping';
    processedRows: number;
    totalRows?: number;
    created: number;
    updated: number;
    skipped: number;
    failed: number;
    updatedAt: string;
  };
  export namespace ProgressJson {
    const Schema = z.object({
      phase: z.enum(['loading', 'staging', 'diffing', 'planning', 'processing', 'sweeping']),
      processedRows: z.number(),
      totalRows: z.number().optional(),
      created: z.number(),
      updated: z.number(),
      skipped: z.number(),
      failed: z.number(),
      updatedAt: z.string(),
    });

    /** DB 等の unknown 値を検証してドメイン型に変換する。不正な値は undefined。 */
    export const FromUnknown = (value: unknown): ProgressJson | undefined => {
      if (value == null) return undefined;
      const result = Schema.safeParse(value);
      return result.success ? result.data : undefined;
    };
  }

  /** 結果詳細の型 */
  export type ResultJson = {
    summary: {
      totalRows: number;
      created: number;
      updated: number;
      skipped: number;
      failed: number;
      /** CSV から抽出された一意な契約キー数（成功行ベース）。旧データ互換のため optional。 */
      extractedContracts?: number;
      /** CSV から抽出された一意な顧客 Identity 数（成功行ベース）。旧データ互換のため optional。 */
      extractedCustomers?: number;
    };
    errors?: {
      row: number;
      column: string;
      message: string;
    }[];
  };

  export type Props = {
    id: string;
    projectId: string;
    source: string;
    importTableDefinitionRevisionId: string;
    status: string;
    fileId: string;
    resultType: string | undefined;
    resultJson: ResultJson | undefined;
    progressJson: ProgressJson | undefined;
    startedAt: string | Date | dayjs | undefined;
    finishedAt: string | Date | dayjs | undefined;
  } & ImmutableEntity.Props;
  export const Construct = (props: Props): ImportTask => {
    return {
      id: Id.FromString(props.id),
      projectId: Project.Id.FromString(props.projectId),
      source: Source.FromString(props.source),
      importTableDefinitionRevisionId: ImportTableDefinitionRevision.Id.FromString(props.importTableDefinitionRevisionId),
      status: Status.FromString(props.status),
      fileId: FileMetadata.Id.FromString(props.fileId),
      resultType: props.resultType ? ResultType.FromString(props.resultType) : undefined,
      resultJson: props.resultJson,
      progressJson: props.progressJson,
      startedAt: props.startedAt ? dayjs(props.startedAt) : undefined,
      finishedAt: props.finishedAt ? dayjs(props.finishedAt) : undefined,
      ...ImmutableEntity.Construct(props),
    };
  };

  export namespace Create {
    export type Context = CommandContext;
    export type Props = Pick<
      ImportTask.Props,
      | 'source' //
      | 'importTableDefinitionRevisionId'
      | 'fileId'
    >;
    export type Params = {
      context: Context;
      props: Props;
    };
  }
  /** 新規作成時は status=Requested で初期化 */
  export const Create = (params: Create.Params): ImportTask => {
    const { context, props } = params;
    return Construct({
      id: Id.Generate(),
      projectId: context.projectId,
      status: 'Requested',
      resultType: undefined,
      resultJson: undefined,
      progressJson: undefined,
      startedAt: undefined,
      finishedAt: undefined,
      ...props,
      ...ImmutableEntity.Create(context),
    });
  };

  /** Started 状態への遷移 */
  export const Start = (task: ImportTask, date?: dayjs): ImportTask => {
    if (task.status !== 'Requested') {
      throw new Error(`Cannot start ImportTask: current status is ${task.status}`);
    }
    return {
      ...task,
      status: 'Started',
      startedAt: date ?? dayjs(new Date()),
    };
  };

  /** Finished 状態への遷移 */
  export const Finish = (task: ImportTask, result: { resultType: ResultType; resultJson: ResultJson }, date?: dayjs): ImportTask => {
    if (task.status !== 'Started') {
      throw new Error(`Cannot finish ImportTask: current status is ${task.status}`);
    }
    return {
      ...task,
      status: 'Finished',
      resultType: result.resultType,
      resultJson: result.resultJson,
      progressJson: undefined,
      finishedAt: date ?? dayjs(new Date()),
    };
  };

  /**
   * 管理者による Finished 状態への強制遷移。
   * `Finish` と異なり現在 status の事前チェックを行わず、
   * `Requested` / `Started` のまま残ったタスクの救済目的で使う。
   */
  export const ForceFinish = (task: ImportTask, result: { resultType: ResultType; resultJson: ResultJson }, date?: dayjs): ImportTask => {
    return {
      ...task,
      status: 'Finished',
      resultType: result.resultType,
      resultJson: result.resultJson,
      progressJson: undefined,
      finishedAt: date ?? dayjs(new Date()),
    };
  };
}
export type ImportTask = ImmutableEntity & {
  projectId: Project.Id;
  id: ImportTask.Id;

  // import 設定
  source: _Source;
  importTableDefinitionRevisionId: ImportTableDefinitionRevision.Id;
  fileId: FileMetadata.Id;

  status: _Status;
  resultType: _ResultType | undefined;
  resultJson: ImportTask.ResultJson | undefined;
  progressJson: ImportTask.ProgressJson | undefined;
  startedAt: dayjs | undefined;
  finishedAt: dayjs | undefined;
};
